import { useMemo } from 'react'
import { isEmpty } from 'lodash/fp'
import {
  BarChart,
  CHOROPLETH_REGION,
  CountryDataChoropleth,
  Heading,
  PieChart,
  RegionDataChoropleth,
  SimpleGrid,
  SimpleGridProps,
  StatCardContainer,
  SummaryProps,
  Text
} from '~/design_system'
import { getPercentage } from '~/common/numberUtils'
import {
  DashboardPageContainer,
  DashboardPageHeader,
  DEFAULT_JOB_STATUS,
  FloatingReportLink,
  StatCard
} from '~/dashboards/common'
import { jobsFilterAdapter } from '~/dashboards/filters/adapters/jobs'
import { jobCategory, jobStatus, jobTitle, jobModule, programTeamEntityFilter } from '~/dashboards/filters/config'
import { useDashboardFilters } from '~/dashboards/filters/hooks'
import { getFilterParams } from '~/dashboards/filters/utils'
import {
  transformSingleRowResultToLabeledValues,
  updateDashboardQueryParams,
  useChoroplethDataV2,
  useDashboardReport
} from '~/dashboards/hooks'
import { featureFlags, useFeatureFlag } from '~/hooks/useFeatureFlag'
import { useDeepEqualEffect2 } from '~/hooks/useDeepEqualEffect'
import { getBusinessUnitFilter, locationFilter, resourceManagerFilter } from '~/work_order_list/filtersConfig'
import { useMsg } from '~/common/localizationUtils'
import { useChartFormat } from '~/design_system/components/charts/format'
import { ChartCard } from '~/dashboards/ChartCard'
import { NamedQueryFilter } from '~/custom_reports/types'
import { useMultiPmoEnabled } from '~/api/programTeam'
import { Filter, typedFilters } from '~/components/v2/FiltersSidebar/types'

const useFiltersConfig = () => {
  const { data: multiPmoEnabled } = useMultiPmoEnabled()

  return useMemo(() => {
    const filters = [
      { ...jobStatus, defaultValue: [DEFAULT_JOB_STATUS] },
      jobCategory,
      jobTitle,
      { ...getBusinessUnitFilter(), id: 'org_unit' },
      { ...resourceManagerFilter, id: 'resource_manager' },
      { ...locationFilter, id: 'worksite' },
      jobModule
    ]

    if (multiPmoEnabled?.enabled) {
      const programTeamEntityFilterConfig = typedFilters(programTeamEntityFilter as Filter)[0]
      filters.splice(-1, 0, programTeamEntityFilterConfig as any)
    }

    return filters
  }, [multiPmoEnabled?.enabled])
}

export const Jobs = () => {
  const msg = useMsg()
  const filtersConfig = useFiltersConfig()

  return (
    <>
      <DashboardPageHeader title={msg('term.job', { count: 999 })} />
      <DashboardPageContainer filtersConfig={filtersConfig}>
        <Charts />
      </DashboardPageContainer>
    </>
  )
}

const useUpdateFiltersEffect = (filters: NamedQueryFilter[]) => {
  const queryFilters = getFilterParams(filters, jobsFilterAdapter)
  const bulkDashboardQueryParams = {
    jobs_by_country: { filters: queryFilters },
    jobs_by_state: { filters: queryFilters },
    jobs_by_resource_manager: { filters: queryFilters },
    positions_comparison: { filters: queryFilters },
    jobs_by_category: { filters: queryFilters },
    job_stat_counts: { filters: queryFilters }
  }

  useDeepEqualEffect2(() => {
    updateDashboardQueryParams(bulkDashboardQueryParams)
  }, [bulkDashboardQueryParams])
}

const Charts = () => {
  const filters = useDashboardFilters({
    org_unit: {
      operator: 'within_hierarchy',
      type: 'bu'
    }
  })
  useUpdateFiltersEffect(filters)

  const { data: showUSMaps } = useFeatureFlag(featureFlags.SHOW_US_MAPS)
  const { data: showWorldMaps } = useFeatureFlag(featureFlags.SHOW_WORLD_MAPS)

  return (
    <>
      <JobApplicationStats mb={4} filters={filters} />
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        {showWorldMaps && <JobsByCountry filters={filters} />}
        {showUSMaps && <JobsByState filters={filters} />}
        <JobsByResourceManager filters={filters} />
        <PositionsComparison filters={filters} />
        <JobsByCategory filters={filters} />
      </SimpleGrid>
    </>
  )
}

type JobApplicationStatsProps = {
  filters: NamedQueryFilter[]
} & SimpleGridProps

const JobApplicationStats = ({ filters, ...props }: JobApplicationStatsProps) => {
  const msg = useMsg()

  const stats = [
    {
      key: 'applied',
      label: msg('term.applied'),
      totalField: 'total_applicants',
      currentField: 'current_applicants'
    },
    {
      key: 'interviewed',
      label: msg('term.interviewed'),
      totalField: 'total_interviewed',
      currentField: 'current_interviewed'
    },
    {
      key: 'offers_released',
      label: msg('term.offers_released'),
      totalField: 'total_offer_released',
      currentField: 'current_offer_released'
    },
    {
      key: 'offers_accepted',
      label: msg('term.offers_accepted'),
      totalField: 'total_accepted',
      currentField: 'current_accepted'
    },
    {
      key: 'onboarded',
      label: msg('term.onboarded'),
      totalField: 'total_onboarded',
      currentField: 'current_onboarded'
    }
  ]

  const reportKey = 'job_stat_counts'

  const queryFilters = getFilterParams(filters, jobsFilterAdapter)
  const { data, isLoading, isError, isFetching } = useDashboardReport({
    id: 'job_analysis',
    queryName: reportKey,
    dashboardQueryParams: { filters: queryFilters },
    useTransform: false
  })

  const localeFormat = useChartFormat('number')

  const extractLocaleFormattedCount = (data: any, field: string) => {
    if (isEmpty(data)) return 0
    if (data[0][field]) {
      return localeFormat(data[0][field])
    }
    return 0
  }

  return (
    <SimpleGrid spacing={4} columns={{ base: 1, sm: 3, md: 5 }} {...props}>
      {stats
        .filter(stat => stat.label)
        .map(stat => (
          <FloatingReportLink reportKey={reportKey} title={stat.label} key={stat.label}>
            <StatCardContainer key={stat.label} isError={isError}>
              <StatCard
                id={stat.key}
                isLoading={isLoading}
                isFetching={isFetching}
                currentData={extractLocaleFormattedCount(data, stat.currentField)}
                labelKey={stat.label}
                helpText={msg('dashboards.stat.out_of_value', {
                  value: extractLocaleFormattedCount(data, stat.totalField)
                })}
              />
            </StatCardContainer>
          </FloatingReportLink>
        ))}
    </SimpleGrid>
  )
}

const JobsByState = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'jobs_by_state'
  const queryFilters = getFilterParams(filters, jobsFilterAdapter)
  const queryData = useChoroplethDataV2({
    id: 'JobsByState',
    queryName: 'jobs_by_state',
    dashboardQueryParams: { filters: queryFilters }
  })

  return (
    <CountryDataChoropleth
      chartConfig={{
        title: msg('dashboards.chart.jobs_by_state.title'),
        subTitle: msg('dashboards.chart.jobs_by_state.sub_title'),
        format: 'number'
      }}
      reportKey={reportKey}
      {...queryData}
    />
  )
}

const JobsByCountry = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'jobs_by_country'
  const queryFilters = getFilterParams(filters, jobsFilterAdapter)
  const queryData = useDashboardReport({
    id: 'JobsByState',
    queryName: reportKey,
    dashboardQueryParams: { filters: queryFilters },
    useTransform: false
  })

  return (
    <RegionDataChoropleth
      region={CHOROPLETH_REGION.world}
      chartConfig={{
        title: msg('dashboards.chart.jobs_by_country.title'),
        subTitle: msg('dashboards.chart.jobs_by_country.sub_title'),
        format: 'number'
      }}
      reportKey={reportKey}
      {...queryData}
    />
  )
}

const JobsByResourceManager = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, jobsFilterAdapter)

  return (
    <ChartCard
      queryId="JobsByResourceManager"
      reportKey="jobs_by_resource_manager"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        format: 'number',
        title: msg('dashboards.chart.jobs_by_resource_manager.title'),
        subTitle: msg('dashboards.chart.jobs_by_resource_manager.sub_title')
      }}
    />
  )
}

export const JobsByCategory = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, jobsFilterAdapter)

  return (
    <ChartCard
      queryId="JobsByCategory"
      reportKey="jobs_by_category"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        format: 'number',
        title: msg('dashboards.chart.jobs_by_category.title'),
        subTitle: msg('dashboards.chart.jobs_by_category.sub_title')
      }}
    />
  )
}

export const PositionsComparison = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'positions_comparison'
  const queryFilters = getFilterParams(filters, jobsFilterAdapter)

  return (
    <ChartCard
      queryId="PositionsComparison"
      reportKey={reportKey}
      filters={queryFilters}
      transformResult={transformSingleRowResultToLabeledValues([
        { id: 'total_onboarded', label: msg('dashboards.chart.positions.filled_positions') },
        { id: 'total_open_positions', label: msg('dashboards.chart.positions.unfilled_positions') }
      ])}
      chart={PieChart}
      chartConfig={{
        title: msg('dashboards.chart.positions.title'),
        subTitle: msg('dashboards.chart.positions.sub_title'),
        colors: ['#29BA9A', 'lightgrey'],
        format: 'number'
      }}
      summary={({ data }: SummaryProps) => {
        const positionsFilled = typeof data[0]?.value === 'string' ? parseInt(data[0].value) : data[0].value
        const positionsUnfilled = typeof data[1]?.value === 'string' ? parseInt(data[1].value) : data[1].value

        const percent = getPercentage(positionsFilled, positionsFilled + positionsUnfilled)

        return (
          data && (
            <>
              <Heading as={Text}>{`${Math.round(percent)}%`}</Heading>
              <Text>{msg('common.ui.position_filled', { count: 999 })}</Text>
            </>
          )
        )
      }}
    />
  )
}
